import type React from "react";
import { useState } from "react";

import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import { usePutPhysicianMedicalReferral } from "api/hooks/usePutPhysicianMedicalReferral";
import { usePutPhysicianPlanOfCare } from "api/hooks/usePutPhysicianPlanOfCare";
import type { CareEvent } from "api/models/CareEvent";
import type { Status } from "api/schemas/physician";
import { BaseButton } from "shared/atoms/BaseButton";
import { ConfirmationPopup } from "shared/atoms/ConfirmationPopup";
import { MedicalNoteTypes } from "types";
import { reportError } from "utils/errorReporting";

interface Props {
  note: CareEvent;
  clearSelectedCareEvent?: () => void;
  patientId?: number;
}

export const MedicalNoteFormButtonsPhysician: React.FC<Props> = ({ note, clearSelectedCareEvent, patientId }) => {
  const { t } = useTranslation();
  const [showMarkAsCompleted, setShowMarkAsCompleted] = useState(false);
  const [showMarkAsDiscarded, setShowMarkAsDiscarded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: mutateAsyncReferral, isLoading: isLoadingReferral } = usePutPhysicianMedicalReferral(patientId);
  const { mutateAsync: mutateAsyncPlanOfCare, isLoading: isLoadingPlanOfCare } = usePutPhysicianPlanOfCare(patientId);

  const isLoading = isLoadingReferral || isLoadingPlanOfCare;

  const updateSatus = async (status: Status) => {
    if (note.label.toLocaleUpperCase() === MedicalNoteTypes.PHYSICIAN_MEDICAL_REFERRAL) {
      mutateAsyncReferral(
        {
          medicalReferralID: note.id,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          data: { ...note, status },
        },
        {
          onSuccess: () => {
            setShowMarkAsCompleted(false);
            setShowMarkAsDiscarded(false);
            if (clearSelectedCareEvent) clearSelectedCareEvent();
          },
          onError: e => {
            reportError("MedicalNoteFormButtonsPhysician.tsx", e);
            setError(t("errors.generic"));
          },
        }
      );
    } else if (note.label.toLocaleUpperCase() === MedicalNoteTypes.PHYSICIAN_PLAN_OF_CARE) {
      mutateAsyncPlanOfCare(
        {
          planOfCareID: note.id,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          data: { ...note, status },
        },
        {
          onSuccess: () => {
            setShowMarkAsCompleted(false);
            setShowMarkAsDiscarded(false);
            if (clearSelectedCareEvent) clearSelectedCareEvent();
          },
          onError: e => {
            reportError("MedicalNoteFormButtonsPhysician.tsx", e);
            setError(t("errors.generic"));
          },
        }
      );
    }
  };

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent={{ md: "flex-end" }}
        gap={2}
        m={4}
        sx={{ "& button": { maxWidth: { md: "400px" }, flex: { md: 0 } } }}
      >
        <BaseButton
          text={t("admin_billing.buttons.discard")}
          onClick={() => setShowMarkAsDiscarded(true)}
          variant="tertiary"
          uppercase
          icon="xmark"
          iconPlacement="start"
        />
        <BaseButton text={t("buttons.sign")} onClick={() => setShowMarkAsCompleted(true)} />
      </Stack>

      <ConfirmationPopup
        isOpen={showMarkAsCompleted}
        title={t("physician.sign", { label: note.label })}
        showCloseButton={false}
        error={error ?? undefined}
        isLoading={isLoading}
        onConfirm={() => updateSatus("accepted")}
        onCancel={() => setShowMarkAsCompleted(false)}
        onClickOutside={() => setShowMarkAsCompleted(false)}
      />

      <ConfirmationPopup
        isOpen={showMarkAsDiscarded}
        title={t("physician.discard", { label: note.label })}
        showCloseButton={false}
        error={error ?? undefined}
        isLoading={isLoading}
        onConfirm={() => updateSatus("rejected")}
        onCancel={() => setShowMarkAsDiscarded(false)}
        onClickOutside={() => setShowMarkAsDiscarded(false)}
      />
    </>
  );
};
